import { AiChatSuggestion } from "components/ai-chats/AiChatSuggestionCard";
import { DragPosition } from "hooks/use-drag-handle";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { AiThreadOriginSurface, AiThreadResourceRef } from "services/aiThread";

/**
 * Client-side category of "what the user is looking at". Drives the
 * suggestion deck shown in the welcome state and the icon shown in the
 * composer's context chip. Distinct from AiThreadOriginSurface (which is
 * persisted on the thread and must match the backend enum).
 */
export type AiChatScreenKind =
  | "conversation"
  | "contact"
  | "campaign"
  | "automation"
  | "report"
  | "calendar"
  | "payments"
  | "template"
  | "default";

export interface AiChatScreenContext {
  /**
   * Client-side category — drives suggestion deck + chip icon.
   */
  kind: AiChatScreenKind;
  /**
   * Origin surface persisted on the thread. Must match the backend enum.
   * For routes the backend doesn't classify (campaigns, payments, …) this
   * falls back to "ai_chats".
   */
  originSurface: AiThreadOriginSurface;
  /**
   * Short, human-readable description of what the agent is "looking at".
   * Example: "Arsen Khadikov · Facebook".
   */
  label: string;
  /**
   * Structured payload forwarded into AiThreadService.postMessage
   * context.resources so the agent can fetch the underlying object.
   */
  resources?: AiThreadResourceRef[];
  /**
   * Dynamic suggestion deck the route wants to show in place of the generic
   * `kind`-based defaults. Routes should bake current state into the prompts
   * (customer name, overdue amount, etc.) so the agent never has to ask
   * "which one?".
   */
  suggestions?: AiChatSuggestion[];
}

interface AiChatContextValue {
  isOpen: boolean;
  open: (screenContext?: AiChatScreenContext) => void;
  close: () => void;
  toggle: () => void;
  screenContext: AiChatScreenContext | null;
  setScreenContext: (next: AiChatScreenContext | null) => void;
  /**
   * Drag offset shared by the launcher and the panel so the bubble keeps the
   * position the user dragged it to when it expands (and vice versa). A ref,
   * not state, because dragging updates it on every pointer move — routing
   * that through context would re-render the whole authenticated app. Each
   * surface mirrors it into local state for its own rendering.
   */
  positionRef: React.MutableRefObject<DragPosition>;
}

const AiChatContext = createContext<AiChatContextValue | null>(null);

interface SurfaceMatch {
  kind: AiChatScreenKind;
  originSurface: AiThreadOriginSurface;
  label: string;
  resourceType?: string;
}

const SURFACE_MAP: Record<string, SurfaceMatch> = {
  inbox: {
    kind: "conversation",
    originSurface: "customer_conversation_detail",
    label: "This conversation",
    resourceType: "conversation",
  },
  contacts: {
    kind: "contact",
    originSurface: "ai_chats",
    label: "This contact",
    resourceType: "contact",
  },
  campaigns: {
    kind: "campaign",
    originSurface: "ai_chats",
    label: "This campaign",
    resourceType: "campaign",
  },
  automations: {
    kind: "automation",
    originSurface: "ai_chats",
    label: "This automation",
    resourceType: "automation",
  },
  analytics: {
    kind: "report",
    originSurface: "ai_chats",
    label: "Analytics",
  },
  calendar: {
    kind: "calendar",
    originSurface: "ai_chats",
    label: "Calendar",
  },
  payments: {
    kind: "payments",
    originSurface: "ai_chats",
    label: "Payments",
  },
  reviews: {
    kind: "conversation",
    originSurface: "ai_chats",
    label: "Reviews",
  },
};

function deriveScreenContextFromPath(
  pathname: string
): AiChatScreenContext | null {
  const segments = pathname.split("/").filter(Boolean);
  // Routes are /:merchantId/<surface>/<id?>/... — segments[0] is the merchant.
  const surfaceSegment = segments[1];
  if (!surfaceSegment) return null;

  // Special-case the templates studio inside settings.
  if (
    surfaceSegment === "settings" &&
    segments[2] === "templates" &&
    segments[3] === "whatsapp"
  ) {
    return {
      kind: "template",
      originSurface: "whatsapp_template_studio",
      label: "WhatsApp template",
      resources: segments[4]
        ? [
            {
              resource_type: "whatsapp_template",
              resource_id: segments[4],
              purpose: "current_view",
            },
          ]
        : undefined,
    };
  }

  const match = SURFACE_MAP[surfaceSegment];
  if (!match) return null;

  const idSegment = segments[2];
  return {
    kind: match.kind,
    originSurface: match.originSurface,
    label: match.label,
    resources:
      idSegment && match.resourceType
        ? [
            {
              resource_type: match.resourceType,
              resource_id: idSegment,
              purpose: "current_view",
            },
          ]
        : undefined,
  };
}

export function AiChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [overrideContext, setOverrideContext] =
    useState<AiChatScreenContext | null>(null);
  const positionRef = useRef<DragPosition>({ x: 0, y: 0 });
  const location = useLocation();

  // Baseline context is derived synchronously from the URL — no useEffect
  // race so the widget never renders with a stale null context. Routes that
  // load richer data (e.g. customer name) layer it on via setScreenContext.
  const baseContext = useMemo(
    () => deriveScreenContextFromPath(location.pathname),
    [location.pathname]
  );

  // Route change invalidates any previous override — it was tied to the old
  // surface, so re-deriving from the URL is the safe default.
  useEffect(() => {
    setOverrideContext(null);
  }, [location.pathname]);

  const screenContext = overrideContext ?? baseContext;

  const setScreenContext = useCallback((next: AiChatScreenContext | null) => {
    setOverrideContext(next);
  }, []);

  const open = useCallback((next?: AiChatScreenContext) => {
    if (next !== undefined) {
      setOverrideContext(next);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo<AiChatContextValue>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      screenContext,
      setScreenContext,
      positionRef,
    }),
    [isOpen, open, close, toggle, screenContext, setScreenContext]
  );

  return (
    <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>
  );
}

export function useAiChat(): AiChatContextValue {
  const ctx = useContext(AiChatContext);
  if (!ctx) {
    throw new Error("useAiChat must be used inside <AiChatProvider>.");
  }
  return ctx;
}
